import React, { useState, useEffect, useContext } from 'react';
import { MultiWalletContext } from '../contexts/MultiWalletContext';
import { alphaAgentService } from '../services/alphaAgentService';
import AlphaSignalCard from './AlphaSignalCard';
import AlphaAgentCard from './AlphaAgentCard';
import TokenNewsFeed from './TokenNewsFeed';
import AutoTradeTab from './autoTrade/AutoTradeTab';
import SmartMoneyHotSignals from './SmartMoneyHotSignals';
import MemeRadarTab from './MemeRadarTab';
import { WalletProvider as PrivyWalletProvider } from '../contexts/PrivyWalletContext'; // 🔧 添加 Privy Provider
import '../styles/AlphaAgentPage.css';

interface AlphaSignalPreview {
  signalId: string;
  tokenSymbol: string;
  signalType: 'LONG' | 'SHORT' | 'NEUTRAL' | 'BUY' | 'SELL';
  confidence: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  currentPrice: number;
  status: 'ACTIVE' | 'HIT_TP' | 'HIT_SL' | 'EXPIRED';
  createdAt: string;
  expiresAt: string;
  // Preview info for list display
  entryMin?: number;
  entryMax?: number;
  stopLoss?: number;
  takeProfit1?: number;
}

interface UsageInfo {
  remainingFreeViews: number;
  isFreeUser: boolean;
}

interface StatsInfo {
  totalSignals: number;
  signalsToday: number;
  signalsThisWeek: number;
  totalAudited: number;
  totalTriggered: number;  // TP+SL总数（用于Win Rate计算）
  hitTP: number;
  hitSL: number;
  winRate: number;
  avgAccuracy: string;
  signalDistribution: {
    [key: string]: {
      count: number;
      avgConfidence: string;
    };
  };
  currentModelVersion: string;
  lastUpdated: string;
}

const AlphaAgentPage: React.FC = () => {
  const { getCurrentAccount } = useContext(MultiWalletContext);
  const account = getCurrentAccount();
  const [activeTab, setActiveTab] = useState<'signals' | 'meme-radar' | 'auto-trade'>('signals');

  const [signals, setSignals] = useState<AlphaSignalPreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'LONG' | 'SHORT' | 'BUY' | 'SELL'>('all');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'all'>('ACTIVE');
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [stats, setStats] = useState<StatsInfo | null>(null);
  const [searchToken, setSearchToken] = useState('');
  const [sortBy, setSortBy] = useState<'time' | 'confidence' | 'winRate'>('confidence');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSignals, setTotalSignals] = useState(0);
  const signalsPerPage = 20;
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    signalId: string;
    cost: number;
    onConfirm: (detail: any) => void;
  } | null>(null);

  useEffect(() => {
    if (account) {
      fetchUsage();
    }
    fetchSignals();
    fetchStats();
  }, [account]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchSignals();
  }, [filter, statusFilter, searchToken]);

  useEffect(() => {
    fetchSignals();
  }, [currentPage, sortBy]);

  // 智能自动刷新：每60秒自动更新价格，页面隐藏时停止
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const startAutoRefresh = () => {
      // 立即刷新一次
      fetchSignals();
      // 启动定时器
      interval = setInterval(() => {
        // console.log('🔄 Auto-refreshing signals...');
        fetchSignals();
      }, 60000); // 60秒
    };

    const stopAutoRefresh = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    // 页面可见性变化监听
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // console.log('⏸️ Page hidden, stopping auto-refresh');
        stopAutoRefresh();
      } else {
        // console.log('▶️ Page visible, starting auto-refresh');
        startAutoRefresh();
      }
    };

    // 添加监听器
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 初始启动（如果页面可见）
    if (!document.hidden) {
      startAutoRefresh();
    }

    // 清理函数
    return () => {
      stopAutoRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [filter, statusFilter, searchToken, currentPage, sortBy]); // 当筛选条件变化时重启定时器

  const fetchUsage = async () => {
    if (!account) {
      console.log('⚠️ No account, skipping usage fetch');
      return;
    }
    try {
      const data = await alphaAgentService.getUsage(account);
      setUsage(data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const fetchSignals = async () => {
    setLoading(true);
    // console.log('🔍 Fetching signals with filters:', {
    //   filter,
    //   statusFilter,
    //   searchToken,
    //   sortBy,
    //   currentPage
    // });
    try {
      const apiParams = {
        limit: signalsPerPage,
        offset: (currentPage - 1) * signalsPerPage,
        signalType: filter === 'all' ? undefined : filter as any,
        status: statusFilter === 'all' ? undefined : statusFilter,
        tokenSymbol: searchToken || undefined,
        sortBy: sortBy, // 传递排序参数给后端
      };
      // console.log('📤 API Request params:', apiParams);

      const response = await alphaAgentService.getSignals(apiParams);

      // console.log('✅ Received signals:', response.signals.length, 'Total:', response.total);
      // console.log('📊 Signal types:', response.signals.map(s => s.signalType).slice(0, 5));

      // 后端已经排序，直接使用返回的数据
      setSignals(response.signals);
      setTotalSignals(response.total);

      // console.log('✅ State updated - signals count:', response.signals.length);
    } catch (error) {
      console.error('Error fetching signals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await alphaAgentService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewDetail = async (signalId: string): Promise<any> => {
    if (!account) {
      alert('Please connect your wallet first');
      return null;
    }

    // 检查是否需要扣费
    const needsPayment = usage && !usage.isFreeUser;

    if (needsPayment) {
      // 返回一个Promise，等待用户确认
      return new Promise((resolve) => {
        setConfirmModal({
          show: true,
          signalId,
          cost: 8,
          onConfirm: resolve
        });
      });
    }

    // 免费查看，直接请求
    return await viewSignalDetail(signalId);
  };

  const viewSignalDetail = async (signalId: string) => {
    try {
      const result = await alphaAgentService.viewSignalDetail(account, signalId);

      // 更新使用次数
      await fetchUsage();

      // 显示提示
      if (!result.charged) {
        console.log(`✓ Free view used (${result.remainingFreeViews} remaining)`);
      } else {
        console.log(`✓ Signal viewed (-${result.cost} credits)`);
      }

      return result.signal;
    } catch (error: any) {
      if (error.response?.status === 402) {
        // 余额不足
        const data = error.response.data;
        alert(
          `Insufficient credits!\n\n` +
          `Required: ${data.required} credits\n` +
          `Current: ${data.current} credits\n\n` +
          `Please purchase more credits to continue.`
        );
      } else {
        alert('Failed to load signal detail: ' + (error.message || 'Unknown error'));
      }
      return null;
    }
  };

  const handleConfirmView = async () => {
    if (!confirmModal) return;

    const { signalId, onConfirm } = confirmModal;
    setConfirmModal(null);

    // 调用 viewSignalDetail 并把结果传递给 resolve
    const detail = await viewSignalDetail(signalId);
    onConfirm(detail);
  };

  const handleCancelView = () => {
    if (!confirmModal) return;

    const { onConfirm } = confirmModal;
    setConfirmModal(null);

    // 取消时返回 null
    onConfirm(null);
  };

  return (
    // 🔧 在最外层包裹 PrivyWalletProvider，避免 AutoTradeTab 内部重复创建
    <PrivyWalletProvider>
      <div className="alpha-agent-page">
        <div className="page-container">
        {/* Main Tab Navigation - 一级Tab */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '48px',
          marginBottom: '32px',
          borderBottom: '2px solid rgba(229, 231, 235, 0.3)',
          padding: '0',
        }}>
          <button
            onClick={() => setActiveTab('signals')}
            style={{
              padding: '12px 0',
              fontSize: '20px',
              fontWeight: activeTab === 'signals' ? '600' : '500',
              border: 'none',
              background: 'none',
              color: activeTab === 'signals' ? '#1877F2' : '#777',
              borderBottom: activeTab === 'signals' ? '2px solid #1877F2' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              marginBottom: '-2px',
            }}
          >
            Alpha Signals
          </button>
          <span style={{ color: 'rgba(200, 200, 200, 0.5)', alignSelf: 'center' }}>|</span>
          <button
            onClick={() => setActiveTab('meme-radar')}
            style={{
              padding: '12px 0',
              fontSize: '20px',
              fontWeight: activeTab === 'meme-radar' ? '600' : '500',
              border: 'none',
              background: 'none',
              color: activeTab === 'meme-radar' ? '#1877F2' : '#777',
              borderBottom: activeTab === 'meme-radar' ? '2px solid #1877F2' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              marginBottom: '-2px',
            }}
          >
            Meme Radar
          </button>
          <span style={{ color: 'rgba(200, 200, 200, 0.5)', alignSelf: 'center' }}>|</span>
          <button
            onClick={() => setActiveTab('auto-trade')}
            style={{
              padding: '12px 0',
              fontSize: '20px',
              fontWeight: activeTab === 'auto-trade' ? '600' : '500',
              border: 'none',
              background: 'none',
              color: activeTab === 'auto-trade' ? '#1877F2' : '#777',
              borderBottom: activeTab === 'auto-trade' ? '2px solid #1877F2' : 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              marginBottom: '-2px',
            }}
          >
            Auto Trade
          </button>
        </div>

        {/* Signals Tab Content */}
        {activeTab === 'signals' && (
          <>
        {/* Strategy Performance - 三卡横排布局 */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* 卡片1: Total Signals - 蓝色渐变 */}
            <div style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.4), 0 8px 24px rgba(59, 130, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span>
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Total Signals
                </div>
              </div>
              <div style={{
                fontSize: '36px',
                fontWeight: '700',
                color: 'white',
                marginBottom: '8px',
                fontFamily: 'Inter, system-ui, sans-serif',
                letterSpacing: '-1px'
              }}>
                {stats.totalSignals.toLocaleString()}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.75)',
                fontWeight: '500'
              }}>
                Today: {stats.signalsToday} | Week: {stats.signalsThisWeek}
              </div>
            </div>

            {/* 卡片2: Win Rate - 紫色渐变 */}
            {stats.totalAudited > 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
                borderRadius: '20px',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(124, 58, 237, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(124, 58, 237, 0.4), 0 8px 24px rgba(124, 58, 237, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(124, 58, 237, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🎯</span>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Win Rate
                  </div>
                </div>
                <div style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  letterSpacing: '-1px'
                }}>
                  {stats.winRate.toFixed(1)}%
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontWeight: '500'
                }}>
                  TP: {stats.hitTP} | SL: {stats.hitSL}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.65)',
                  marginTop: '4px'
                }}>
                  {stats.totalTriggered} triggered / {stats.totalAudited} audited
                </div>
              </div>
            ) : (
              <div style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
                borderRadius: '20px',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(124, 58, 237, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <span style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</span>
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '600'
                }}>
                  Win rate available after<br/>24-48 hours of auditing
                </div>
              </div>
            )}

            {/* 卡片3: Avg Accuracy - 灰蓝渐变 */}
            {stats.totalAudited > 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1E293B 0%, #475569 100%)',
                borderRadius: '20px',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(30, 41, 59, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(71, 85, 105, 0.5), 0 8px 24px rgba(30, 41, 59, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(30, 41, 59, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🧠</span>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Avg Accuracy
                  </div>
                </div>
                <div style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  letterSpacing: '-1px'
                }}>
                  {stats.avgAccuracy}%
                </div>
              </div>
            ) : (
              <div style={{
                background: 'linear-gradient(135deg, #1E293B 0%, #475569 100%)',
                borderRadius: '20px',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(30, 41, 59, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <span style={{ fontSize: '32px', marginBottom: '8px' }}>📊</span>
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '600'
                }}>
                  Accuracy metrics<br/>coming soon
                </div>
              </div>
            )}
          </div>
        )}

        <div className="page-content">
          {/* Left Column - Agent Card */}
          <div className="left-column">
            <AlphaAgentCard account={account} />

            {/* Market News Feed */}
            <div style={{ marginTop: '24px' }}>
              <TokenNewsFeed
                limit={10}
                showFilters={true}
                autoRefresh={true}
              />
            </div>

            {/* Risk Warning */}
            <div className="warning-card" style={{ marginTop: '24px' }}>
              <h4 className="warning-title">⚠️ Risk Warning</h4>
              <p className="warning-text">
                Trading cryptocurrencies carries high risk. Signals are for informational
                purposes only and not financial advice. Always do your own research (DYOR)
                and never invest more than you can afford to lose.
              </p>
            </div>
          </div>

          {/* Right Column - Signals */}
          <div className="right-column">
            {/* 信号筛选面板 - Signal Filter Panel */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '24px',
              marginBottom: '32px',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)'
            }}>
              {/* 第一行：搜索框 + Reset 按钮 */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                {/* 搜索框 */}
                <div style={{ flex: '1 1 auto', minWidth: '200px', maxWidth: '100%' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search token / pair..."
                    value={searchToken}
                    onChange={(e) => setSearchToken(e.target.value.toUpperCase())}
                    className="search-input-modern"
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 16px',
                      fontSize: '14px',
                      border: '1px solid #CBD5E1',
                      borderRadius: '12px',
                      outline: 'none',
                      background: 'white',
                      color: '#1E293B !important',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#5A6AE6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(90, 106, 230, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#CBD5E1';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Reset 按钮 */}
                <button
                  onClick={() => {
                    setSearchToken('');
                    setFilter('all');
                    setStatusFilter('ACTIVE');
                    setSortBy('confidence');
                    fetchSignals();
                  }}
                  style={{
                    height: '44px',
                    padding: '0 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: '1px solid #CBD5E1',
                    borderRadius: '12px',
                    background: 'white',
                    color: '#64748B',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F1F5F9';
                    e.currentTarget.style.borderColor = '#94A3B8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#CBD5E1';
                  }}
                >
                  Reset 🔄
                </button>
              </div>

              {/* 第二行：条件筛选（Signal Type） */}
              <div style={{
                display: 'inline-flex',
                gap: '6px',
                padding: '4px',
                background: '#F1F5F9',
                borderRadius: '9999px',
                marginBottom: '16px'
              }}>
                {(['all', 'LONG', 'SHORT', 'BUY', 'SELL'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      console.log('🔘 Filter button clicked:', type);
                      setFilter(type as 'all' | 'LONG' | 'SHORT' | 'BUY' | 'SELL');
                    }}
                    style={{
                      height: '36px',
                      padding: '0 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '9999px',
                      background: filter === type ? '#6366F1' : 'transparent',
                      color: filter === type ? 'white' : '#64748B',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== type) {
                        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                        e.currentTarget.style.color = '#6366F1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== type) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#64748B';
                      }
                    }}
                  >
                    {type === 'all' ? 'All' : type}
                  </button>
                ))}
              </div>

              {/* 第三行：Status & Sort（左对齐） */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '24px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                {/* Status */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '12px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    Status:
                  </span>
                  {(['ACTIVE', 'all'] as const).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        console.log('🔘 Status filter clicked:', status);
                        setStatusFilter(status as 'ACTIVE' | 'all');
                      }}
                      style={{
                        height: '36px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: statusFilter === status ? 'none' : '1px solid #CBD5E1',
                        borderRadius: '9999px',
                        background: statusFilter === status ? '#6366F1' : 'white',
                        color: statusFilter === status ? 'white' : '#64748B',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (statusFilter !== status) {
                          e.currentTarget.style.background = '#F1F5F9';
                          e.currentTarget.style.color = '#6366F1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (statusFilter !== status) {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#64748B';
                        }
                      }}
                    >
                      {status === 'ACTIVE' ? 'Active' : 'All'}
                    </button>
                  ))}
                </div>

                {/* Sort */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '12px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    Sort:
                  </span>
                  {(['time', 'confidence', 'winRate'] as const).map((sort) => (
                    <button
                      key={sort}
                      onClick={() => {
                        console.log('🔘 Sort button clicked:', sort);
                        setSortBy(sort as 'time' | 'confidence' | 'winRate');
                      }}
                      style={{
                        height: '36px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: sortBy === sort ? 'none' : '1px solid #CBD5E1',
                        borderRadius: '9999px',
                        background: sortBy === sort ? '#6366F1' : 'white',
                        color: sortBy === sort ? 'white' : '#64748B',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (sortBy !== sort) {
                          e.currentTarget.style.background = '#F1F5F9';
                          e.currentTarget.style.color = '#6366F1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (sortBy !== sort) {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#64748B';
                        }
                      }}
                    >
                      {sort === 'time' ? 'Latest ▼' : sort === 'confidence' ? 'Confidence' : 'Win Rate'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 第四行：快捷键提示（可选） */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #E2E8F0',
                fontSize: '11px',
                color: '#94A3B8',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <span>⌨️ Shortcuts:</span>
                <span><kbd style={{
                  padding: '2px 6px',
                  background: '#F1F5F9',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>/</kbd> Focus</span>
                <span><kbd style={{
                  padding: '2px 6px',
                  background: '#F1F5F9',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>r</kbd> Reset</span>
                <span><kbd style={{
                  padding: '2px 6px',
                  background: '#F1F5F9',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>a</kbd> Toggle Status</span>
                <span><kbd style={{
                  padding: '2px 6px',
                  background: '#F1F5F9',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>s</kbd> Sort</span>
              </div>
            </div>

            {/* Signals List */}
            <div className="signals-section">
              {loading && signals.length === 0 ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading signals...</p>
                </div>
              ) : signals.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No Signals Yet</h3>
                  <p>
                    {statusFilter === 'ACTIVE'
                      ? 'No active signals at the moment. Check back soon!'
                      : 'No signals match your filters.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="signals-list">
                    {signals.map((signal) => (
                      <AlphaSignalCard
                        key={signal.signalId}
                        signal={signal}
                        onViewDetail={handleViewDetail}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalSignals > signalsPerPage && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '32px',
                      padding: '24px',
                      background: 'linear-gradient(to bottom, rgba(249, 250, 251, 0.5) 0%, transparent 100%)',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      {/* Page Info */}
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ color: '#111827', fontWeight: '700', fontSize: '16px' }}>
                          Page {currentPage}
                        </span>
                        <span>of</span>
                        <span style={{ color: '#111827', fontWeight: '700', fontSize: '16px' }}>
                          {Math.ceil(totalSignals / signalsPerPage)}
                        </span>
                        <span style={{
                          marginLeft: '12px',
                          padding: '4px 10px',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {totalSignals} signals
                        </span>
                      </div>

                      {/* Pagination Controls */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        {/* First Page Button */}
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            borderRadius: '8px',
                            background: currentPage === 1
                              ? 'rgba(0, 0, 0, 0.05)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: currentPage === 1 ? '#9ca3af' : 'white',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: currentPage === 1 ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                            opacity: currentPage === 1 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== 1) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = currentPage === 1 ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)';
                          }}
                        >
                          ⏮ First
                        </button>

                        {/* Previous Button */}
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            borderRadius: '8px',
                            background: currentPage === 1
                              ? 'rgba(0, 0, 0, 0.05)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: currentPage === 1 ? '#9ca3af' : 'white',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: currentPage === 1 ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                            opacity: currentPage === 1 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== 1) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = currentPage === 1 ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)';
                          }}
                        >
                          ← Previous
                        </button>

                        {/* Page Numbers */}
                        {(() => {
                          const totalPages = Math.ceil(totalSignals / signalsPerPage);
                          const pageNumbers = [];
                          const maxVisible = 5;

                          let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                          let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                          if (endPage - startPage + 1 < maxVisible) {
                            startPage = Math.max(1, endPage - maxVisible + 1);
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pageNumbers.push(
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  border: i === currentPage ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  background: i === currentPage
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                                    : 'white',
                                  color: i === currentPage ? 'white' : '#374151',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  boxShadow: i === currentPage ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                                }}
                                onMouseEnter={(e) => {
                                  if (i !== currentPage) {
                                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (i !== currentPage) {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                  }
                                }}
                              >
                                {i}
                              </button>
                            );
                          }
                          return pageNumbers;
                        })()}

                        {/* Next Button */}
                        <button
                          onClick={() => setCurrentPage(prev => prev + 1)}
                          disabled={currentPage >= Math.ceil(totalSignals / signalsPerPage)}
                          style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            borderRadius: '8px',
                            background: currentPage >= Math.ceil(totalSignals / signalsPerPage)
                              ? 'rgba(0, 0, 0, 0.05)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? '#9ca3af' : 'white',
                            cursor: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                            opacity: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage < Math.ceil(totalSignals / signalsPerPage)) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)';
                          }}
                        >
                          Next →
                        </button>

                        {/* Last Page Button */}
                        <button
                          onClick={() => setCurrentPage(Math.ceil(totalSignals / signalsPerPage))}
                          disabled={currentPage >= Math.ceil(totalSignals / signalsPerPage)}
                          style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            borderRadius: '8px',
                            background: currentPage >= Math.ceil(totalSignals / signalsPerPage)
                              ? 'rgba(0, 0, 0, 0.05)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? '#9ca3af' : 'white',
                            cursor: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                            opacity: currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage < Math.ceil(totalSignals / signalsPerPage)) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = currentPage >= Math.ceil(totalSignals / signalsPerPage) ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)';
                          }}
                        >
                          Last ⏭
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
          </>
        )}

        {/* Meme Radar Tab Content */}
        {activeTab === 'meme-radar' && (
          <MemeRadarTab />
        )}

        {/* Auto Trade Tab Content */}
        {activeTab === 'auto-trade' && (
          <AutoTradeTab />
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="modal-overlay" onClick={handleCancelView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">💳 Confirm Payment</h3>
              <button className="modal-close" onClick={handleCancelView}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-message">
                Viewing this signal will cost <strong>{confirmModal.cost} credits</strong>.
              </p>
              <p className="modal-note">
                You will receive complete analysis including entry points, stop-loss, and take-profit levels.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={handleCancelView}>
                Cancel
              </button>
              <button className="modal-btn confirm" onClick={handleConfirmView}>
                Confirm (-{confirmModal.cost} credits)
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </PrivyWalletProvider>
  );
};

export default AlphaAgentPage;
